/**
 * Sends unexpected browser errors to our own back office (plan 8.4, safety nets). At most ten
 * different ones per page load; noise from browser extensions and known harmless warnings is ignored.
 */
const seen = new Set<string>();
const IGNORE = [/ResizeObserver loop/, /^Script error\.?$/, /chrome-extension:|moz-extension:|safari-extension:/, /Failed to fetch|NetworkError|Load failed/];

export function reportError(source: 'app' | 'booking', error: unknown) {
  const e = error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');
  const message = (e.message || String(error)).slice(0, 500);
  const stack = e.stack?.slice(0, 4000) ?? null;
  if (IGNORE.some((re) => re.test(message) || (stack && re.test(stack)))) return;
  const key = `${message}|${stack?.split('\n')[1] ?? ''}`;
  if (seen.has(key) || seen.size >= 10) return;
  seen.add(key);
  try {
    void fetch('/api/v1/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source, message, stack, path: location.pathname, release: import.meta.env.VITE_RELEASE ?? null }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Reporting must never cause an error of its own.
  }
}

export function installErrorReporting(source: 'app' | 'booking') {
  window.addEventListener('error', (ev) => reportError(source, ev.error ?? ev.message));
  window.addEventListener('unhandledrejection', (ev) => reportError(source, ev.reason));
}
